<template>
    <div v-if="content" class="bg-gray-300">
        <ProductGuideBlock v-if="contentForProductGuide" :content="contentForProductGuide" :is-first-block="true"/>
        <CompatabilityGuideBlock v-else-if="isCompatibilityGuide" :content="content" :is-first-block="true"/>
        <CableGuideBlock v-else-if="isCableGuide && content" :content="content" :is-first-block="true"/>
        <ModalsContainer/>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import { CableGuideBlockViewModel, ProductGuideBlockViewModel } from '@/types/contentServerContract';
import Api from '@/project/http/Api.service';
import { ProductGuideConfigurationViewModel } from '@/types/apiServerContract';
import ModalsContainer from '@/project/modals/ModalsContainer.vue';
import serverContextService from '@/core/serverContext.service';
import httpService from '@/core/http/http.service';
import dictionaryService from '@/core/translation/dictionary.service';
import { UrlQueryKey } from '@/core/urlQueryKey';
import urlHelperService from '@/core/urlHelper.service';

const ProductGuideBlock = defineAsyncComponent(() => import('@/project/app/blocks/productguide/ProductGuideBlock.vue'));
const CableGuideBlock = defineAsyncComponent(() => import('@/project/app/blocks/cableguide/CableGuideBlock.vue'));
const CompatabilityGuideBlock = defineAsyncComponent(() => import('@/project/app/blocks/CompatabilityGuide/CompatabilityGuideBlock.vue'));

const route = useRoute();
const content = ref<ProductGuideBlockViewModel | CableGuideBlockViewModel | null>(null);
const guideConfiguration = ref<ProductGuideConfigurationViewModel>();

const isCompatibilityGuide = computed((): boolean => {
    return !!content.value && content.value['guideId'] === 'compatibility';
});

const isCableGuide = computed(() => {
    return !!content.value && content.value['guideId'] === 'cable';
});

const isProductGuide = computed(() => {
    return !!content.value && content.value['guideId'] !== 'cable' && content.value['guideId'] !== 'compatibility';
});

const contentForProductGuide = computed(() => {
    return isProductGuide.value && content.value as ProductGuideBlockViewModel;
});

const createContent = async(): Promise<ProductGuideBlockViewModel | CableGuideBlockViewModel> => {
    let productPageSize = parseInt(urlHelperService.getQueryParam(UrlQueryKey.productGuideProductPageSize) || '30', 10);
    if (isNaN(productPageSize)) {
        productPageSize = 30;
    }

    const guideId = urlHelperService.getQueryParam(UrlQueryKey.productGuideGuideId) || guideConfiguration.value?.type || 'all';
    const hideStock = (guideConfiguration.value?.hideStock ?? (urlHelperService.getQueryParam(UrlQueryKey.hideStock) === 'true'));
    const showFacets = guideConfiguration.value?.showFacets ?? (!(urlHelperService.getQueryParam(UrlQueryKey.hideFacets)) || false);
    const startPage = guideConfiguration.value?.startPage ?? urlHelperService.getQueryParam(UrlQueryKey.productGuideModeKey);
    const externalProductLinkText = guideConfiguration.value?.externalProductLinkText ?? urlHelperService.getQueryParam(UrlQueryKey.productGuideExternalButtonText);
    const externalProductLink = guideConfiguration.value?.externalProductLink ?? urlHelperService.getQueryParam(UrlQueryKey.productGuideExternalButtonLink);
    const externalStylesheet = guideConfiguration.value?.externalStylesheet ?? urlHelperService.getQueryParam(UrlQueryKey.css);
    const language = guideConfiguration.value?.language ?? (urlHelperService.getQueryParam(UrlQueryKey.language) || undefined);
    const theme = guideConfiguration.value?.theme?.toLowerCase() ?? (urlHelperService.getQueryParam(UrlQueryKey.theme) || '');
    const allowedBrands = guideConfiguration.value?.allowedBrands ?? [];

    serverContextService.externalProductGuideSettings = {
        hideStock,
        externalProductLink,
        externalProductLinkText,
        externalStylesheet,
        language,
        theme,
        showFacets,
        startPage,
        allowedBrands,
        externalId: urlHelperService.getQueryParam(UrlQueryKey.productGuideExternalId)
    };

    updateExternalStylesheet();
    await updateDictionary();

    return {
        guideId: guideId.toLowerCase(),
        productPageSize: productPageSize,
        appendToPreviousBlock: false,
        startInStepByStep: startPage === 'step-by-step'
    } as ProductGuideBlockViewModel;
};

const getConfiguration = async(externalId: string) => {
    const config = await Api.productGuide.getConfiguration(externalId);
    guideConfiguration.value = config;
    const overrideProductUrl = urlHelperService.getQueryParam(UrlQueryKey.productGuideOverrideProductUrl);

    if (overrideProductUrl) {
        guideConfiguration.value.externalProductLink = overrideProductUrl;
    }
};

const updateExternalStylesheet = () => {
    const externalStylesheet = serverContextService.externalProductGuideSettings?.externalStylesheet;

    if (externalStylesheet) {
        let linkElement = document.querySelector('#productGuideCss');
        if (!linkElement) {
            linkElement = document.createElement('link');
            linkElement.id = 'productGuideCss';
            linkElement.setAttribute('rel', 'stylesheet');
            linkElement.setAttribute('type', 'text/css');
            document.head.appendChild(linkElement);
        }

        linkElement.setAttribute('href', externalStylesheet);
    }
};

const updateDictionary = async() => {
    const language = serverContextService.externalProductGuideSettings?.language;

    if (language) {
        const dictionary = await httpService.getRaw<{[key:string]: string }>('/app/dictionary?siteBaseUrl=/' + language);
        if (dictionary) {
            window.servercontext.dictionary = dictionary;
            dictionaryService.reinitialize();
        }
    }
};

const init = async() => {
    const externalId = urlHelperService.getQueryParam(UrlQueryKey.productGuideExternalId);
    if (externalId) {
        await getConfiguration(externalId);
    }
    content.value = await createContent();
};

watch(() => route, () => {
    const guideId = (content.value as ProductGuideBlockViewModel)?.guideId;
    if (!guideId) {
        createContent();
    }
});

onMounted(() => {
    init();
});
</script>
